import Modal from '@/common/components/Modal';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { formatCurrency, formatDecimalWithGroupedUnit } from '@/common/utils/formatters';
import { FormTransformers } from '@/common/utils/formTransformers';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { EntryProductCategory } from './EntryProductCategory';
import { useCreateGovernmentAllocationEntryProductCategory } from '@/features/governmentAllocations/hooks/useCreateGovernmentAllocationEntryProductCategory';
import { entryProductCategoryCurrencySchema, entryProductCategoryQuantitySchema, GovernmentAllocationEntryProductCategoryCurrencyValidationSchema, GovernmentAllocationEntryProductCategoryQuantityValidationSchema } from '@/features/governmentAllocations/schemas/update.schema';
import { ClientCompanyAllowedCategoryAndProductData } from '@/features/companyBranches/types/allowedCategoriesAndProducts.type';
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type';
import { EntryProductCategoryQuantity } from '@/features/governmentAllocations/pages/Update/components/EntryProductCategoryQuantity';

interface CreateEntryProductCategoryModalProps {
  companyBranchId?: string;
  allowedCategorySummary?: ClientCompanyAllowedCategoryAndProductData & { id: string } | undefined;
  onDismiss: VoidFunction;
  controlledBy?: CompanyContractConsumptionControlledBy;
  governmentAllocationCurrencyRestricted: boolean | undefined;
}

const CreateEntryProductCategoryModal = ({
  companyBranchId,
  allowedCategorySummary,
  onDismiss,
  controlledBy,
  governmentAllocationCurrencyRestricted,
}: CreateEntryProductCategoryModalProps) => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const schema = controlledBy === CompanyContractConsumptionControlledBy.Quantity ? entryProductCategoryQuantitySchema : entryProductCategoryCurrencySchema;

  const {
    mutate: createEntryProductCategory,
    isPending: createEntryProductCategoryIsPending,
  } = useCreateGovernmentAllocationEntryProductCategory({
    governmentAllocationId: id,
    governmentAllocationCurrencyRestricted,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocation', id] });
      onDismiss();
      reset();
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const formMethods = useForm<GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      governmentAllocationCurrencyRestricted,
    },
  });

  const {
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
  } = formMethods;

  const entryProducts = watch('entry_products');

  const setTotalValue = () => {
    setTimeout(() => {
      if (!entryProducts || entryProducts.length === 0) {
        return
      }

      if (controlledBy === CompanyContractConsumptionControlledBy.Quantity) {
        const entryProductsTotalQuantitySum = (entryProducts || []).reduce((sum, _, index) => {
          const productTotalQuantity = watch(`entry_products.${index}.total_quantity` as `entry_products.${number}.total_value`);
          return sum + (productTotalQuantity ? unFormatUnknownUnit(productTotalQuantity) : 0);
        }, 0);

        setValue('total_quantity' as 'total_value', formatDecimalWithGroupedUnit(
          entryProductsTotalQuantitySum,
          entryProducts as GovernmentAllocationEntryProductCategoryQuantityValidationSchema['entry_products'],
          (p) => p.product.unit_for_quantity,
        ));
      }

      const entryProductsTotalValueSum = (entryProducts || []).reduce((sum, _, index) => {
        const productTotalValue = watch(`entry_products.${index}.total_value`);
        return sum + (productTotalValue ? unFormatCurrency(productTotalValue) : 0);
      }, 0);

      setValue('total_value', formatCurrency(entryProductsTotalValueSum));
    });
  }

  const onSubmit: SubmitHandler<any> = (form) => createEntryProductCategory({
    ...form,
    product_category: allowedCategorySummary,
  });

  useEffect(() => {
    if (allowedCategorySummary?.id) {
      reset({
        governmentAllocationCurrencyRestricted,
      });
    }
  }, [allowedCategorySummary?.id]);

  return (
    <>
      <Modal
        isOpen={!!allowedCategorySummary}
        setIsOpen={(value) => {
          if (!value) {
            onDismiss();

            setTimeout(reset, 500);
          }
        }}
        title={`Detalhando crédito por serviço`}
        showClose
        useCancellableFooter
        className="min-w-[640px] overflow-hidden"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        confirmButtonClassName="bg-primary-blue-hover"
        cancelButtonClassName='bg-white-200'
        disabled={createEntryProductCategoryIsPending}
        confirmButtonIsLoading={createEntryProductCategoryIsPending}
        bodyClassName='max-h-[80vh] overflow-auto'
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <div className="max-h-[480px] min-h-[360px] overflow-auto flex flex-col gap-6 pe-2">
                {allowedCategorySummary && controlledBy === CompanyContractConsumptionControlledBy.Quantity ? (
                  <EntryProductCategoryQuantity
                    entryProductCategory={{
                      id: '',
                      product_category: {
                        id: allowedCategorySummary.id,
                        display_name: allowedCategorySummary.display_name,
                        products_count: ''
                      },
                      total_value: '',
                      total_quantity: '',
                      entry_products: [],
                    }}
                    allowedCategorySummary={allowedCategorySummary}
                    companyBranchId={companyBranchId}
                    disabled={false}
                    onTotalValueChange={setTotalValue}
                    governmentAllocationCurrencyRestricted={governmentAllocationCurrencyRestricted}
                  />
                ) : allowedCategorySummary && (
                  <EntryProductCategory
                    entryProductCategory={{
                      id: '',
                      product_category: {
                        id: allowedCategorySummary.id,
                        display_name: allowedCategorySummary.display_name,
                        products_count: ''
                      },
                      total_value: '',
                      entry_products: [],
                    }}
                    allowedCategorySummary={allowedCategorySummary}
                    companyBranchId={companyBranchId}
                    disabled={false}
                    onTotalValueChange={setTotalValue}
                  />
                )}
              </div>
            </div>
          </FormProvider>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
}

export default CreateEntryProductCategoryModal;
